import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  allowedFieldTypeMatching,
  availableFieldRelationTypes,
  integerOperator,
} from 'constants/Field.constants';
import _ from 'lodash';
import { fetchFields, fieldsSelector } from 'slices/field.slice';
import { useAppDispatch } from 'slices/store';
import { EFieldType, EMatchMode, EOperator } from '@timeedit/types/lib/enums';
import type { TField, TFieldRelation } from '@timeedit/types/lib/types';
import { TEDrawerSection, TEInput } from '@timeedit/ui-components';
import { Cascader } from '@timeedit/ui-components/antd';

type PropsConfiguration = {
  fieldRelation: TFieldRelation;
  onChange: Function;
};

type TFieldCascaderOption = {
  [key: string]: {
    label?: string;
    options: {
      value: string;
      label: string;
    }[];
  };
};

const FieldRelationConfiguration = ({
  fieldRelation,
  onChange,
}: PropsConfiguration) => {
  const dispatch = useAppDispatch();
  const fields: TField[] = useSelector(fieldsSelector);
  useEffect(() => {
    dispatch(fetchFields());
  }, [dispatch]);

  // Gets the fieldType on the choosen field
  const getFieldTypeOnField = (field: any) => {
    const result = fields.find((f: any) => f.id === field);
    if (result) {
      return result.fieldType;
    }
    return field;
  };

  // EVENT HANDLER
  const onChangeSelectHandler = (property: string, value?: string) => {
    onChange(property, value);
  };

  const isIntegerField =
    getFieldTypeOnField(fieldRelation.sourceField) === EFieldType.INTEGER &&
    getFieldTypeOnField(fieldRelation.matchField) === EFieldType.INTEGER;
  const isCategoryField =
    getFieldTypeOnField(fieldRelation.sourceField) === EFieldType.CATEGORY &&
    getFieldTypeOnField(fieldRelation.matchField) === EFieldType.CATEGORY;

  const fieldsSortedOnType = useMemo(
    () =>
      fields
        .filter((field) =>
          availableFieldRelationTypes.includes(field.fieldType as EFieldType)
        )
        .reduce((acc: TFieldCascaderOption, field: TField) => {
          if (acc[field.fieldType]) {
            return {
              ...acc,
              [field.fieldType]: {
                ...acc[field.fieldType],
                options: [
                  ...acc[field.fieldType].options,
                  {
                    value: field.id,
                    label: field.extId,
                  },
                ].sort((a, b) => a.label.localeCompare(b.label)),
              },
            };
          }
          return {
            ...acc,
            [field.fieldType]: {
              label: field.fieldType,
              options: [
                {
                  value: field.id,
                  label: field.extId,
                },
              ],
            },
          };
        }, {}),
    [fields]
  );

  const assertIntegerField = (value: unknown): value is EOperator =>
    Object.values(EOperator).includes(value as EOperator);

  const assertCategoryField = (value: unknown): value is EMatchMode =>
    Object.values(EMatchMode).includes(value as EMatchMode);

  const getOperatorOptions = useMemo(() => {
    if (isIntegerField) {
      return Object.values(EOperator).map((value) => ({
        value,
        label: integerOperator[value],
      }));
    }
    if (isCategoryField) {
      return Object.values(EMatchMode).map((value) => ({
        value,
        label: _.startCase(value.toLocaleLowerCase()),
      }));
    }
    return [];
  }, [isIntegerField, isCategoryField]);

  const comparisonType =
    (isIntegerField && 'operator') || (isCategoryField && 'matchMode') || null;

  return (
    <div className="configuration">
      <TEDrawerSection label="Relation Specification">
        <div style={{ width: '100%' }}>
          <TEInput.LabelWrapper
            label="Relation"
            tooltip="Select the fields that should be related, and the type of relation between them."
          >
            <div
              style={{
                gap: '4px',
                display: 'flex',
                flexDirection: 'column',
                alignContent: 'stretch',
                overflow: 'hidden',
              }}
            >
              <Cascader
                options={Object.values(fieldsSortedOnType).map((field) => ({
                  value: field.label,
                  label: _.startCase(field.label.toLocaleLowerCase()),
                  children: field.options,
                  // If sourceField is not set or the combination of current iterated field and selected sourceField is allowed, enable the field
                  disabled: !(
                    !fieldRelation.sourceField ||
                    allowedFieldTypeMatching.some(
                      ([match, source]) =>
                        match === field.label &&
                        source ===
                          getFieldTypeOnField(fieldRelation.sourceField)
                    )
                  ),
                }))}
                value={
                  fieldRelation.matchField
                    ? [
                        getFieldTypeOnField(fieldRelation.matchField),
                        fieldRelation.matchField,
                      ]
                    : undefined
                }
                showSearch
                popupMatchSelectWidth={false}
                size="small"
                style={{ width: 'unset' }}
                placeholder="Select match field"
                onChange={(value?: [string, string]) => {
                  if (!value) {
                    onChangeSelectHandler('matchField');
                    return;
                  }
                  onChangeSelectHandler('matchField', value[1]);
                }}
                data-testid="match-field"
              />
              <TEInput.Select
                popupMatchSelectWidth={false}
                wrapperStyle={{ width: 'unset' }}
                style={{ width: '100%' }}
                value={
                  comparisonType ? fieldRelation[comparisonType] : undefined
                }
                disabled={!isIntegerField && !isCategoryField}
                onChange={(value: EOperator | EMatchMode) => {
                  if (assertIntegerField(value)) {
                    onChangeSelectHandler('operator', value);
                    return;
                  } else if (assertCategoryField(value)) {
                    onChangeSelectHandler('matchMode', value);
                  }
                }}
                options={getOperatorOptions}
                data-testid="operator"
              />
              <Cascader
                options={Object.values(fieldsSortedOnType).map((field) => ({
                  value: field.label,
                  label: _.startCase(field.label.toLocaleLowerCase()),
                  children: field.options,
                  // If matchField is not set or the combination of current iterated field and selected matchField is allowed, enable the field
                  disabled: !(
                    !fieldRelation.matchField ||
                    allowedFieldTypeMatching.some(
                      ([match, source]) =>
                        match === field.label &&
                        source === getFieldTypeOnField(fieldRelation.matchField)
                    )
                  ),
                }))}
                value={
                  fieldRelation.sourceField
                    ? [
                        getFieldTypeOnField(fieldRelation.sourceField),
                        fieldRelation.sourceField,
                      ]
                    : undefined
                }
                showSearch
                popupMatchSelectWidth={false}
                size="small"
                style={{ width: 'unset' }}
                placeholder="Select source field"
                onChange={(value: [string, string]) => {
                  if (!value) {
                    onChangeSelectHandler('sourceField', undefined);
                    return;
                  }
                  onChangeSelectHandler('sourceField', value[1]);
                }}
                data-testid="source-field"
              />
            </div>
          </TEInput.LabelWrapper>
        </div>
      </TEDrawerSection>
    </div>
  );
};

export default FieldRelationConfiguration;
